/// <amd-module name="VSS/Redux/Utils/ObservableUtils" />
import { effects, EventChannel, eventChannel } from "redux-saga";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { IObservable } from "VSS/Core/Observable";

export type EventArgs<T> = { value: T; type?: string };

/**
 * Creates an event channel from an observable
 * @param observable The observable to watch
 * @param actions An optional array of specific actions to listen to. If blank, all actions will be subscribed to
 */
export function createObservableChannel<T>(observable: IObservable<T>, actions?: string[]): EventChannel<EventArgs<T>> {
    return eventChannel(emitter => {
        function listener(type?: string): (newValue: T) => void {
            return (newValue: T) => emitter({ value: newValue, type });
        }

        if (actions) {
            const listeners = actions.map(a => {
                const _lis = listener(a);
                observable.subscribe(_lis, a);
                return _lis;
            });

            return () => listeners.forEach(l => observable.unsubscribe(l));
        } else {
            const _lis = listener();
            observable.subscribe(_lis);
            return () => observable.unsubscribe(_lis);
        }
    });
}

type ActionCreator<T> = (payload: T) => ActionWithPayload<any, T>;

/**
 * Create an observable channel listener for use in a saga.
 * Given an observable channel, take all events from that channel, and dispatch the given action
 * @param channel The channel to listen to
 * @param actionCreator The action(s) to dispatch for all events on the channel. Either a single action can be passed, which will be fired for all events,
 * or a dictionary of observable action types to Flux actions
 */
export function createObservableChannelListener<T>(
    channel: EventChannel<EventArgs<T>>,
    actionCreator: ActionCreator<T> | { [key: string]: ActionCreator<T> }
) {
    return function*() {
        try {
            while (true) {
                const event: EventArgs<T> = (yield effects.take(channel)) as any;
                if (typeof actionCreator === "function") {
                    // Only one handler
                    yield effects.put(actionCreator(event.value));
                } else {
                    const { value, type } = event;
                    if (type) {
                        const handler = actionCreator[type];
                        if (handler) {
                            yield effects.put(handler(value));
                        }
                    }
                }
            }
        } finally {
            if (yield effects.cancelled()) {
                // Close the channel, this saga was unmounted
                channel.close();
            }
        }
    };
}

/**
 * Create a saga function that listens to an observable and dispatches actions
 * @param observable The observable to listen to
 * @param actionCreator The action(s) to dispatch for all events on the channel. Either a single action can be passed, which will be fired for all events,
 * or a dictionary of observable event types to Flux actions
 */
export function createObservableSaga<T>(observable: IObservable<T>, actionCreator: ActionCreator<T> | { [key: string]: ActionCreator<T> }) {
    let channel: EventChannel<EventArgs<T>>;
    if (typeof actionCreator === "function") {
        channel = createObservableChannel(observable);
    } else {
        const eventTypes = Object.keys(actionCreator);
        channel = createObservableChannel(observable, eventTypes);
    }

    return createObservableChannelListener(channel, actionCreator);
}
